<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useEvents } from '@/composables/useEvents'
import {
  EVENT_FONTS,
  EVENT_TEMPLATES,
  EVENT_THEMES,
  type EventFontCategory,
  type EventFontSlug,
  type EventTemplateSlug,
  type EventThemeSlug,
} from '@/types/events'

function isAxiosError(e: unknown): e is { response: { status: number; data: { errors?: Record<string, string[]> } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: unknown }).response !== null
  )
}

const { t } = useI18n()
const route = useRoute()
const { event, fetchEvent, updateEvent } = useEvents()

const eventSlug = route.params['eventSlug'] as string

const form = ref<{
  template: EventTemplateSlug
  theme: EventThemeSlug
  font_heading: EventFontSlug
  font_body: EventFontSlug
}>({
  template: 'classic',
  theme: 'granite',
  font_heading: 'inter',
  font_body: 'open-sans',
})

const loading = ref(false)
const errors = ref<Record<string, string[]>>({})
const generalError = ref('')
const successMessage = ref<string | null>(null)

// Ordered list of categories for consistent rendering
const CATEGORY_ORDER: EventFontCategory[] = [
  'sans-serif',
  'serif',
  'display',
  'monospace',
  'handwriting',
]

const CATEGORY_LABEL_KEYS: Record<EventFontCategory, string> = {
  'sans-serif':  'events.appearance.font_category_sans_serif',
  'serif':       'events.appearance.font_category_serif',
  'display':     'events.appearance.font_category_display',
  'monospace':   'events.appearance.font_category_monospace',
  'handwriting': 'events.appearance.font_category_handwriting',
}

/**
 * Fonts grouped by category, preserving CATEGORY_ORDER for <optgroup> rendering.
 */
const fontsByCategory = computed(() =>
  CATEGORY_ORDER.map((cat) => ({
    category: cat,
    label: t(CATEGORY_LABEL_KEYS[cat]),
    fonts: EVENT_FONTS.filter((f) => f.category === cat),
  }))
)

function getFamily(slug: EventFontSlug): string {
  return EVENT_FONTS.find((f) => f.slug === slug)?.family ?? 'sans-serif'
}

function buildGoogleFontsUrl(heading: EventFontSlug, body: EventFontSlug): string {
  const headingFamily = getFamily(heading).replace(/ /g, '+')
  const bodyFamily = getFamily(body).replace(/ /g, '+')
  const families = heading === body
    ? `family=${headingFamily}`
    : `family=${headingFamily}&family=${bodyFamily}`
  return `https://fonts.googleapis.com/css2?${families}&display=swap`
}

/**
 * Inject a single <link> that loads every font in the catalogue so each
 * option's preview paragraph renders immediately when selected without a
 * network round-trip.
 */
function injectAllCatalogueFonts(): void {
  const id = 'event-appearance-all-fonts'
  if (document.getElementById(id)) return
  const families = EVENT_FONTS.map((f) => `family=${f.family.replace(/ /g, '+')}`).join('&')
  const link = document.createElement('link')
  link.id = id
  link.rel = 'stylesheet'
  link.href = `https://fonts.googleapis.com/css2?${families}&display=swap`
  document.head.appendChild(link)
}

/**
 * Keep the preview <link> updated when the selection changes so the font is
 * already loaded by the time the preview paragraph renders.
 */
function injectFontLink(heading: EventFontSlug, body: EventFontSlug): void {
  const id = 'event-appearance-font-link'
  let link = document.getElementById(id) as HTMLLinkElement | null
  if (!link) {
    link = document.createElement('link')
    link.id = id
    link.rel = 'stylesheet'
    document.head.appendChild(link)
  }
  link.href = buildGoogleFontsUrl(heading, body)
}

onMounted(async () => {
  injectAllCatalogueFonts()
  if (!event.value || event.value.slug !== eventSlug) {
    await fetchEvent(eventSlug)
  }
  if (event.value) {
    form.value = {
      template: event.value.template,
      theme: event.value.theme,
      font_heading: event.value.font_heading,
      font_body: event.value.font_body,
    }
  }
  injectFontLink(form.value.font_heading, form.value.font_body)
})

watch([() => form.value.font_heading, () => form.value.font_body], ([heading, body]) => {
  injectFontLink(heading, body)
})

async function handleSubmit(): Promise<void> {
  errors.value = {}
  generalError.value = ''
  successMessage.value = null
  loading.value = true
  try {
    await updateEvent(eventSlug, {
      ...form.value,
      name: event.value?.name ?? '',
    })
    successMessage.value = t('events.appearance.save_success')
    setTimeout(() => { successMessage.value = null }, 4000)
  } catch (e: unknown) {
    if (isAxiosError(e)) {
      if (e.response.status === 422 && e.response.data.errors) {
        errors.value = e.response.data.errors
      } else {
        generalError.value = t('common.error_generic')
      }
    } else {
      generalError.value = t('common.error_generic')
    }
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 sm:p-8">

    <!-- Loading spinner -->
    <div v-if="!event" class="flex justify-center py-12">
      <div class="h-6 w-6 animate-spin rounded-full border-2 border-slate-600 border-t-emerald-400" />
    </div>

    <form v-else @submit.prevent="handleSubmit">

      <!-- General error -->
      <div
        v-if="generalError"
        class="mb-6 rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400"
      >
        {{ generalError }}
      </div>

      <!-- Success -->
      <div
        v-if="successMessage"
        class="mb-6 rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-4 py-3 text-sm text-emerald-400"
      >
        {{ successMessage }}
      </div>

      <!-- ── Template section ──────────────────────────────────────────── -->
      <section class="mb-8">
        <h2 class="text-sm font-semibold text-white mb-1">
          {{ t('events.appearance.template_heading') }}
        </h2>
        <p class="text-xs text-slate-400 mb-4">
          {{ t('events.appearance.template_description') }}
        </p>

        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <button
            v-for="tmpl in EVENT_TEMPLATES"
            :key="tmpl.slug"
            type="button"
            class="rounded-xl border p-4 text-left transition-colors"
            :class="form.template === tmpl.slug
              ? 'border-emerald-500 ring-2 ring-emerald-500 bg-slate-900/60'
              : 'border-slate-600 bg-slate-900/40 hover:border-slate-500'"
            @click="form.template = tmpl.slug"
          >
            <!-- Schematic thumbnail -->
            <svg
              viewBox="0 0 120 80"
              class="w-full rounded mb-3 bg-slate-800"
              aria-hidden="true"
            >
              <!-- Header bar -->
              <rect x="0" y="0" width="120" height="12" rx="2" fill="#334155" />
              <!-- Hero block -->
              <rect x="4" y="16" width="112" height="24" rx="2" fill="#1e3a4c" />
              <!-- Content block 1 -->
              <rect x="4" y="44" width="112" height="8" rx="2" fill="#1e293b" />
              <!-- Content block 2 -->
              <rect x="4" y="56" width="80" height="8" rx="2" fill="#1e293b" />
            </svg>
            <span class="text-xs font-medium text-slate-200">
              {{ t(`events.appearance.template_${tmpl.slug}`) }}
            </span>
          </button>
        </div>

        <p v-if="errors['template']" class="mt-2 text-xs text-red-400">
          {{ errors['template']?.[0] }}
        </p>
      </section>

      <hr class="border-slate-700 mb-8" />

      <!-- ── Theme section ─────────────────────────────────────────────── -->
      <section class="mb-8">
        <h2 class="text-sm font-semibold text-white mb-1">
          {{ t('events.appearance.theme_heading') }}
        </h2>
        <p class="text-xs text-slate-400 mb-4">
          {{ t('events.appearance.theme_description') }}
        </p>

        <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <button
            v-for="thm in EVENT_THEMES"
            :key="thm.slug"
            type="button"
            class="rounded-xl border p-3 text-left transition-colors"
            :class="form.theme === thm.slug
              ? 'border-emerald-500 ring-2 ring-emerald-500 bg-slate-900/60'
              : 'border-slate-600 bg-slate-900/40 hover:border-slate-500'"
            @click="form.theme = thm.slug"
          >
            <div class="flex gap-1.5 mb-2">
              <span
                v-for="(colour, i) in thm.swatches"
                :key="i"
                class="inline-block w-6 h-6 rounded"
                :style="{ backgroundColor: colour }"
              />
            </div>
            <span class="text-xs font-medium text-slate-200">
              {{ t(`events.appearance.theme_${thm.slug}`) }}
            </span>
          </button>
        </div>

        <p v-if="errors['theme']" class="mt-2 text-xs text-red-400">
          {{ errors['theme']?.[0] }}
        </p>
      </section>

      <hr class="border-slate-700 mb-8" />

      <!-- ── Font section ───────────────────────────────────────────────── -->
      <section class="mb-8">
        <h2 class="text-sm font-semibold text-white mb-1">
          {{ t('events.appearance.fonts_heading') }}
        </h2>
        <p class="text-xs text-slate-400 mb-4">
          {{ t('events.appearance.fonts_description') }}
        </p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Heading font -->
          <div>
            <label class="block text-xs font-medium text-slate-300 mb-1.5">
              {{ t('events.appearance.font_heading_label') }}
            </label>
            <select
              v-model="form.font_heading"
              class="w-full rounded-lg bg-slate-900/60 border border-slate-600 text-slate-200 text-sm px-3 py-2 focus:outline-none focus:border-emerald-500"
            >
              <optgroup
                v-for="group in fontsByCategory"
                :key="group.category"
                :label="group.label"
              >
                <option
                  v-for="f in group.fonts"
                  :key="f.slug"
                  :value="f.slug"
                  :style="{ fontFamily: `'${f.family}', sans-serif` }"
                >
                  {{ f.family }}
                </option>
              </optgroup>
            </select>
            <p
              class="mt-3 text-xl text-slate-200 truncate"
              :style="{ fontFamily: `'${getFamily(form.font_heading)}', sans-serif` }"
            >
              {{ t('events.appearance.font_preview_heading') }}
            </p>
            <p v-if="errors['font_heading']" class="mt-1 text-xs text-red-400">
              {{ errors['font_heading']?.[0] }}
            </p>
          </div>

          <!-- Body font -->
          <div>
            <label class="block text-xs font-medium text-slate-300 mb-1.5">
              {{ t('events.appearance.font_body_label') }}
            </label>
            <select
              v-model="form.font_body"
              class="w-full rounded-lg bg-slate-900/60 border border-slate-600 text-slate-200 text-sm px-3 py-2 focus:outline-none focus:border-emerald-500"
            >
              <optgroup
                v-for="group in fontsByCategory"
                :key="group.category"
                :label="group.label"
              >
                <option
                  v-for="f in group.fonts"
                  :key="f.slug"
                  :value="f.slug"
                  :style="{ fontFamily: `'${f.family}', sans-serif` }"
                >
                  {{ f.family }}
                </option>
              </optgroup>
            </select>
            <p
              class="mt-3 text-base text-slate-200 truncate"
              :style="{ fontFamily: `'${getFamily(form.font_body)}', sans-serif` }"
            >
              {{ t('events.appearance.font_preview_body') }}
            </p>
            <p v-if="errors['font_body']" class="mt-1 text-xs text-red-400">
              {{ errors['font_body']?.[0] }}
            </p>
          </div>
        </div>
      </section>

      <!-- ── Save bar ───────────────────────────────────────────────────── -->
      <div class="flex justify-end">
        <button
          type="submit"
          class="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 transition-colors text-white text-sm font-semibold"
          :disabled="loading"
        >
          <span v-if="!loading">{{ t('events.appearance.save') }}</span>
          <span v-else>{{ t('common.saving') }}</span>
        </button>
      </div>

    </form>
  </div>
</template>
